import { inject } from "ui-common/di";
import type { IWritableLiveEventAccessRequestState, ILiveEventAccessRequestService } from "./types";
import type { IRpcService } from "ui-common/services/rpc/types";
import type { IAuthProvider } from "ui-common/services/auth/types";
import type { GetLiveEventAccessRequestResult } from "api-contracts/rpc/live-event-access-request";
import type { RequestLiveEventAccessResult } from "api-contracts/rpc/live-event-access-request";

export class LiveEventAccessRequestService implements ILiveEventAccessRequestService {
  @inject<IWritableLiveEventAccessRequestState>("writableLiveEventAccessRequestState")
  private state!: IWritableLiveEventAccessRequestState;

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  @inject<IAuthProvider>("authProvider")
  private authProvider!: IAuthProvider;

  private pendingLoad: Promise<void> | undefined;

  loadRequestStatus(): void {
    if (!this.authProvider.isAuthenticated()) return;
    if (this.pendingLoad) return;

    this.state.loading.set(true);
    this.pendingLoad = this.doLoad();
  }

  private async doLoad(): Promise<void> {
    try {
      const result = await this.rpcService.call<GetLiveEventAccessRequestResult>("get-live-event-access-request", {});
      this.state.requestStatus.set(result.status);
    } catch (error) {
      console.error("[LiveEventAccessRequestService] Error loading request status:", error);
    } finally {
      this.state.loading.set(false);
      this.pendingLoad = undefined;
    }
  }

  async requestAccess(): Promise<boolean> {
    if (this.state.hasPendingRequest()) return true;

    this.state.submitting.set(true);
    try {
      const result = await this.rpcService.call<RequestLiveEventAccessResult>("request-live-event-access", {});
      this.state.requestStatus.set(result.status);
      return true;
    } catch (error) {
      console.error("[LiveEventAccessRequestService] Error requesting access:", error);
      return false;
    } finally {
      this.state.submitting.set(false);
    }
  }
}
